package com.example.histoire.web

import kotlin.math.pow
import org.jetbrains.compose.web.css.CSSColorValue
import org.jetbrains.compose.web.css.Color

data class ResolvedReaderTheme(
    val primary: CSSColorValue,
    val secondary: CSSColorValue,
    val label: CSSColorValue,
    val storyBody: CSSColorValue,
    val storyTitle: CSSColorValue,
    /** Vrai si le texte principal est clair (champs « verre » foncés). */
    val lightForeground: Boolean,
    val backdropEnabled: Boolean,
    val backdropCss: String,
)

fun resolveReaderTheme(
    prefs: ReaderUiPrefs,
    imageLuminance: Double?,
    hasCustomPhotoBackground: Boolean,
    enabled: Boolean,
): ResolvedReaderTheme {
    if (!enabled) {
        return ResolvedReaderTheme(
            primary = Color.white,
            secondary = Color("#E2E8F0"),
            label = Color("#E2E8F0"),
            storyBody = Color("#F8FAFC"),
            storyTitle = Color.white,
            lightForeground = true,
            backdropEnabled = false,
            backdropCss = "transparent",
        )
    }

    val autoUseDarkText =
        hasCustomPhotoBackground && imageLuminance != null && imageLuminance >= 0.55

    val (primary, lightForeground) =
        when (prefs.textMode) {
            "custom" -> {
                val c = normalizeHexColor(prefs.textColorHex)
                Color(c) to isHexColorLight(c)
            }
            "light" -> Color("#F8FAFC") to true
            "dark" -> Color("#0F172A") to false
            else -> {
                if (autoUseDarkText) Color("#0F172A") to false
                else Color("#F8FAFC") to true
            }
        }

    val secondary =
        if (lightForeground) Color("#E2E8F0") else Color("#334155")
    val label = secondary
    val storyBody =
        if (lightForeground) Color("#F8FAFC") else Color("#1E293B")

    val backdropCss =
        if (prefs.backdropEnabled) {
            val rgb = hexToRgbTriplet(prefs.backdropColorHex) ?: Triple(15, 23, 42)
            val a = prefs.backdropOpacityPercent.coerceIn(0, 100) / 100.0
            "rgba(${rgb.first}, ${rgb.second}, ${rgb.third}, $a)"
        } else {
            "transparent"
        }

    return ResolvedReaderTheme(
        primary = primary,
        secondary = secondary,
        label = label,
        storyBody = storyBody,
        storyTitle = primary,
        lightForeground = lightForeground,
        backdropEnabled = prefs.backdropEnabled,
        backdropCss = backdropCss,
    )
}

private val hex6 = Regex("^#([0-9A-Fa-f]{6})$")
private val hex3 = Regex("^#([0-9A-Fa-f]{3})$")

fun normalizeHexColor(raw: String): String {
    val t = raw.trim()
    if (hex6.matches(t)) return t.uppercase()
    val m3 = hex3.matchEntire(t) ?: return "#F8FAFC"
    val s = m3.groupValues[1]
    return ("#" + s.map { "$it$it" }.joinToString("")).uppercase()
}

private fun isHexColorLight(hex: String): Boolean {
    val rgb = hexToRgbTriplet(hex) ?: return true
    val l =
        relativeSrgbLuminanceByte(rgb.first, rgb.second, rgb.third)
    return l >= 0.55
}

private fun hexToRgbTriplet(hex: String): Triple<Int, Int, Int>? {
    val n = normalizeHexColor(hex)
    val m = hex6.matchEntire(n) ?: return null
    val h = m.groupValues[1]
    val r = h.substring(0, 2).toInt(16)
    val g = h.substring(2, 4).toInt(16)
    val b = h.substring(4, 6).toInt(16)
    return Triple(r, g, b)
}

private fun relativeSrgbLuminanceByte(r: Int, g: Int, b: Int): Double {
    fun lin(c: Int): Double {
        val cs = c.coerceIn(0, 255) / 255.0
        return if (cs <= 0.03928) cs / 12.92 else ((cs + 0.055) / 1.055).pow(2.4)
    }
    val R = lin(r)
    val G = lin(g)
    val B = lin(b)
    return 0.2126 * R + 0.7152 * G + 0.0722 * B
}
